import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { validationApi } from '../services/validationApi';
import type { ValidationStatusResponse, ValidationDecision, Finding } from '../types/validation';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { formatIsoDate } from '../utils/formatDateValue';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  SparklesIcon,
  PencilSquareIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

type DrawerTab = 'all' | 'blockers' | 'resolution' | 'autofilled' | 'notes' | 'sources' | 'history';

const decisionColors: Record<ValidationDecision, string> = {
  VALIDATED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
  NEEDS_REVIEW: 'bg-yellow-100 text-yellow-700',
};

const decisionIcons: Record<ValidationDecision, React.ReactNode> = {
  VALIDATED: <CheckCircleIcon className="h-4 w-4 text-green-500" />,
  REJECTED: <XCircleIcon className="h-4 w-4 text-red-500" />,
  NEEDS_REVIEW: <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />,
};

function decisionLabel(decision: ValidationDecision) {
  if (decision === 'NEEDS_REVIEW') return 'Needs Review';
  return decision.charAt(0) + decision.slice(1).toLowerCase();
}

function confidenceBar(confidence: number, size: 'sm' | 'lg' = 'sm') {
  const pct = Math.round(confidence * 100);
  const color = pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className={`bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden ${size === 'lg' ? 'w-32 h-2.5' : 'w-full h-2'}`}>
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`font-medium whitespace-nowrap ${size === 'lg' ? 'text-sm text-gray-700 dark:text-gray-300' : 'text-xs text-gray-600 dark:text-gray-400'}`}>{pct}%</span>
    </div>
  );
}

// A qualitative word for how much of the case is documented — informational, so it doesn't read as
// a "fill the remaining fields" task the way a raw fraction does.
function coverageBand(pct: number): string {
  if (pct >= 90) return 'Extensive';
  if (pct >= 70) return 'Good';
  if (pct >= 40) return 'Partial';
  return 'Limited';
}

// Structural documentation-coverage bar (a separate axis from confidence — how fully documented, not
// how trustworthy). Shows the qualitative band rather than a fraction to avoid implying a to-do.
function completenessBar(c: { completeness: number; filled: number; total: number } | null | undefined, size: 'sm' | 'lg' = 'sm') {
  if (!c) return null;
  const pct = Math.round(c.completeness * 100);
  return (
    <div className="flex items-center gap-2">
      <div className={`bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden ${size === 'lg' ? 'w-32 h-2.5' : 'w-full h-2'}`}>
        <div className="h-full bg-sky-500 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className={`font-medium whitespace-nowrap ${size === 'lg' ? 'text-sm text-gray-700 dark:text-gray-300' : 'text-xs text-gray-600 dark:text-gray-400'}`}>{coverageBand(pct)}</span>
    </div>
  );
}

// Friendly labels for the completeness "not documented" field paths, grouped by section so the
// raw dotted keys (and the per-violation repeats) read as plain language.
const MISSING_LABELS: Record<string, string> = {
  description: 'Description', caseStartDate: 'Incident start date', caseStartDatePrecision: 'Start-date precision',
  country: 'Country', region: 'Region', city: 'City', indigenousGroup: 'Indigenous group',
  violationTypes: 'Violation types', otherViolationTypes: 'Other violation types', sourceInformation: 'Source',
  communityImpact: 'Community impact', familyImpact: 'Family impact', 'caseEndDate(concluded)': 'End date',
  affectedIndividualsCount: 'Total individuals affected', 'affectedCount>0': 'People affected',
  groupName: 'Group name', indigenousGroupOfVictim: 'Indigenous group', name: 'Name', gender: 'Gender',
  violationType: 'Violation type', perpetratorKnown_answered: 'Perpetrator known', perpetratorTypes: 'Perpetrator types',
  whyVictimsKilled: 'Why victims killed', howKillingCarriedOut: 'How killing carried out',
  accusations: 'Accusations', whoFiledAccusations: 'Who filed accusations',
  affectedCount: 'People count', genderBreakdown: 'Gender breakdown', ageBreakdown: 'Age breakdown',
};
const missingSection = (p: string): string =>
  p.startsWith('victim.violation.') ? 'Per-violation demographics'
    : p.startsWith('victim.') ? 'Victim'
    : p.startsWith('violation.') ? 'Violation'
    : 'Case';
const missingLabel = (p: string): string => {
  const leaf = p.split('.').slice(-1)[0];
  return MISSING_LABELS[leaf] || leaf.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
};
// Group missing fields by section and collapse identical labels into a count (× N).
function groupMissing(paths: string[]): { section: string; items: { label: string; count: number }[] }[] {
  const order = ['Case', 'Victim', 'Violation', 'Per-violation demographics'];
  const bySection = new Map<string, Map<string, number>>();
  for (const p of paths) {
    const sec = missingSection(p);
    const lab = missingLabel(p);
    if (!bySection.has(sec)) bySection.set(sec, new Map());
    const m = bySection.get(sec)!;
    m.set(lab, (m.get(lab) || 0) + 1);
  }
  return order.filter((s) => bySection.has(s)).map((s) => ({
    section: s,
    items: Array.from(bySection.get(s)!.entries()).map(([label, count]) => ({ label, count })),
  }));
}

// The four hard extraction anchors — pass/fail, separate from coverage %.
const ANCHOR_LABELS: Record<string, string> = {
  incidentStartDate: 'Start date',
  namedIndigenousGroup: 'Indigenous group',
  location: 'Location',
  identifiableVictim: 'Identifiable victim',
};
function AnchorsBadge({ comp }: { comp?: { anchorsPass?: boolean; missingAnchors?: string[] } | null }) {
  if (!comp || comp.anchorsPass === undefined) return null;
  if (comp.anchorsPass) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" title="All four required extraction anchors are present">
        ✓ Anchors complete
      </span>
    );
  }
  const missing = (comp.missingAnchors || []).map((a) => ANCHOR_LABELS[a] || a).join(', ');
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" title="Required extraction anchors (Case Baseline / Data Dictionary) are missing">
      Missing anchor{(comp.missingAnchors || []).length > 1 ? 's' : ''}: {missing}
    </span>
  );
}

// A labeled row: "<label>" left, bar right.
function labeledBar(label: string, bar: React.ReactNode) {
  if (!bar) return null;
  return (
    <div>
      <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
      {bar}
    </div>
  );
}

// "Why this score" — the deterministic deductions that produced the confidence number.
function ScoreBreakdown({ breakdown }: { breakdown?: { method: string; aiSelfEstimate?: number; deductions?: Array<{ category: string; weight: number; message?: string | null }> } | null }) {
  if (!breakdown || breakdown.method !== 'deterministic') return null;
  const ded = breakdown.deductions || [];
  const label = (c: string) => c.replace(/_/g, ' ').toLowerCase().replace(/^./, (x) => x.toUpperCase());
  return (
    <div className="rounded-md border border-gray-200 dark:border-gray-700 p-2.5 text-xs">
      <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">How this score was computed</div>
      {ded.length === 0 ? (
        <div className="text-gray-500 dark:text-gray-400">Started at 100% — no deductions (clean, corroborated case).</div>
      ) : (
        <ul className="space-y-0.5">
          <li className="text-gray-500 dark:text-gray-400">Start: 100%</li>
          {ded.map((d, i) => (
            <li key={i} className="flex items-baseline gap-2">
              <span className="text-red-600 dark:text-red-400 font-medium whitespace-nowrap">−{Math.round(d.weight * 100)}%</span>
              <span className="text-gray-600 dark:text-gray-300">{label(d.category)}{d.message ? ` — ${d.message}` : ''}</span>
            </li>
          ))}
        </ul>
      )}
      {typeof breakdown.aiSelfEstimate === 'number' && (
        <div className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">AI self-estimate (cross-check): {Math.round(breakdown.aiSelfEstimate * 100)}%</div>
      )}
    </div>
  );
}

function getValueByPath(obj: any, path: string): unknown {
  // Split on dots and brackets: "victims[uuid].field" → ["victims", "uuid", "field"]
  const keys = path.replace(/\[([^\]]+)\]/g, '.$1').split('.');
  return keys.reduce((curr, key) => {
    if (curr === null || curr === undefined) return undefined;
    // If current value is an array and key is not a numeric index, look up by id
    if (Array.isArray(curr) && !/^\d+$/.test(key)) {
      return curr.find((item: any) => item?.id === key);
    }
    return curr[key];
  }, obj);
}

const fieldLabels: Record<string, string> = {
  country: 'Country', region: 'Region', city: 'City', specificLocation: 'Specific Location',
  description: 'Description', familyImpact: 'Family Impact', communityImpact: 'Community Impact',
  incidentTime: 'Incident Time', caseStartDate: 'Start Date', caseEndDate: 'End Date',
  reportedDate: 'Reported Date', violationTypes: 'Violation Types', otherViolationTypes: 'Other Violation Types',
  perpetratorKnown: 'Perpetrator Known', perpetratorTypes: 'Perpetrator Types',
  perpetratorDetails: 'Perpetrator Details', legalCaseFiled: 'Legal Case Filed',
  legalCaseDetails: 'Legal Case Details', legalCaseWhyNot: 'Legal Case (Why Not)',
  stateActionUndertaken: 'State Action Undertaken', stateActionDetails: 'State Action Details',
  stateActionWhyNot: 'State Action (Why Not)', whyVictimsCriminalized: 'Why Victims Criminalized',
  accusations: 'Accusations', policiesOrLawsUsed: 'Policies/Laws Used',
  whoFiledAccusations: 'Who Filed Accusations', accusationsLegallyFiled: 'Accusations Legally Filed',
  courtFilingLocation: 'Court Filing Location', courtFilingDetails: 'Court Filing Details',
  decisionOnAccusations: 'Decision on Accusations', decisionDetails: 'Decision Details',
  victimsInPrison: 'Victims in Prison', prisonConditions: 'Prison Conditions',
  victimsSituationIfNotInPrison: 'Victims Situation', whyVictimsKilled: 'Why Victims Killed',
  howKillingCarriedOut: 'How Killing Carried Out', cause: 'Cause',
  harassmentPrior: 'Prior Harassment', harassmentPriorDetails: 'Prior Harassment Details',
  investigationConducted: 'Investigation Conducted', investigationDetails: 'Investigation Details',
  investigationWhyNot: 'Investigation (Why Not)', advocacyUndertaken: 'Advocacy Undertaken',
  advocacyDetails: 'Advocacy Details', advocacyWhyNot: 'Advocacy (Why Not)',
  indigenousGroup: 'Indigenous Group', indigenousTerritory: 'Indigenous Territory',
  firstName: 'First Name', lastName: 'Last Name', gender: 'Gender',
  communityRole: 'Community Role', additionalInfo: 'Additional Info',
  indigenousGroupOfVictim: 'Indigenous Group', name: 'Name', type: 'Type',
  affiliation: 'Affiliation', notes: 'Notes',
};

const violationTypeLabels: Record<string, string> = {
  KILLING: 'Killing', CRIMINALIZATION: 'Criminalization', OTHER: 'Other',
};

/** Turn "violationDetails[uuid].accusations" into "Criminalization > Accusations" */
function formatFieldPath(path: string, caseData?: Record<string, any>): string {
  const segments = path.replace(/\[([^\]]+)\]/g, '.$1').split('.');
  const parts: string[] = [];

  let i = 0;
  while (i < segments.length) {
    const seg = segments[i];

    if (seg === 'violationDetails') {
      // Next segment is the ID, look up the violation type
      const id = segments[i + 1];
      let label = 'Violation Details';
      if (id && caseData?.violationDetails) {
        const detail = (caseData.violationDetails as any[]).find((d: any) => d.id === id);
        if (detail?.violationType) {
          label = violationTypeLabels[detail.violationType] || detail.violationType;
        }
      }
      parts.push(label);
      i += 2; // skip collection + id
    } else if (seg === 'victims') {
      const id = segments[i + 1];
      let label = 'Victims';
      if (id && caseData?.victims) {
        const victim = (caseData.victims as any[]).find((v: any) => v.id === id);
        if (victim) {
          const name = [victim.firstName, victim.lastName].filter(Boolean).join(' ');
          if (name) label = name;
        }
      }
      parts.push(label);
      i += 2;
    } else if (seg === 'perpetrators') {
      const id = segments[i + 1];
      let label = 'Perpetrators';
      if (id && caseData?.perpetrators) {
        const perp = (caseData.perpetrators as any[]).find((p: any) => p.id === id);
        if (perp?.name) label = perp.name;
      }
      parts.push(label);
      i += 2;
    } else {
      // Regular field name
      parts.push(fieldLabels[seg] || seg.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim());
      i++;
    }
  }

  return parts.join(' > ');
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return 'none';
  if (Array.isArray(value) && value.length === 0) return 'none';
  const asDate = formatIsoDate(value);
  if (asDate) return asDate;
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

/** Format an UPPER_SNAKE_CASE enum value to Title Case. */
function formatEnumValue(val: string): string {
  return val.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

/** Check if a string looks like an UPPER_SNAKE_CASE enum value. */
function isEnumValue(val: string): boolean {
  return /^[A-Z][A-Z0-9_]+$/.test(val);
}

/** Summarise a single record object into key–value pairs, dropping nulls and internal fields. */
function summarizeRecord(obj: Record<string, unknown>): Array<{ label: string; raw: unknown }> {
  const skip = new Set(['id', 'caseId', 'createdAt', 'updatedAt', 'isGroup', 'groupName', 'groupSize',
    'affectedIndividualsCount', 'affectedFamiliesCount', 'affectedHomesCount', 'affectedGroupsCount',
    'affectedCommunitiesCount', 'affectedVillagesCount', 'affectedMaleCount', 'affectedFemaleCount',
    'affected0to12Count', 'affected13to25Count', 'affected26to35Count', 'affected36to59Count', 'affected60PlusCount',
    'violationType']);
  const labelMap: Record<string, string> = {
    firstName: 'First Name', lastName: 'Last Name', gender: 'Gender',
    dateOfBirth: 'Date of Birth', ageBracket: 'Age Bracket',
    dependentType: 'Dependent Type', dependentDetails: 'Dependent Details',
    communityRole: 'Community Role', indigenousGroupOfVictim: 'Indigenous Group',
    additionalInfo: 'Additional Info', name: 'Name', type: 'Type',
    affiliation: 'Affiliation', position: 'Position', notes: 'Notes',
    isOrganization: 'Organization', organizationHead: 'Organization Head',
    perpetratorKnown: 'Perpetrator Known', perpetratorTypes: 'Perpetrator Types',
    perpetratorDetails: 'Perpetrator Details', legalCaseFiled: 'Legal Case Filed',
    legalCaseDetails: 'Legal Case Details', legalCaseWhyNot: 'Legal Case (Why Not)',
    stateActionUndertaken: 'State Action Undertaken', stateActionDetails: 'State Action Details',
    stateActionWhyNot: 'State Action (Why Not)', whyVictimsCriminalized: 'Why Victims Criminalized',
    accusations: 'Accusations', policiesOrLawsUsed: 'Policies/Laws Used',
    whoFiledAccusations: 'Who Filed Accusations', whoFiledAccusationsDetails: 'Who Filed Accusations Details',
    accusationsLegallyFiled: 'Accusations Legally Filed', courtFilingLocation: 'Court Filing Location',
    courtFilingDetails: 'Court Filing Details', decisionOnAccusations: 'Decision on Accusations',
    decisionDetails: 'Decision Details', victimsInPrison: 'Victims in Prison',
    prisonConditions: 'Prison Conditions', victimsSituationIfNotInPrison: 'Victims Situation',
    whyVictimsKilled: 'Why Victims Killed', howKillingCarriedOut: 'How Killing Carried Out',
    harassmentPrior: 'Prior Harassment', harassmentPriorDetails: 'Prior Harassment Details',
    cause: 'Cause',
  };
  const entries: Array<{ label: string; raw: unknown }> = [];
  for (const [k, v] of Object.entries(obj)) {
    if (skip.has(k) || v === null || v === undefined || v === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    const label = labelMap[k] || k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
    entries.push({ label, raw: v });
  }
  return entries;
}

/** Render a field value with appropriate formatting (badges for enums/arrays, text for strings). */
function FieldValue({ value }: { value: unknown }) {
  if (typeof value === 'boolean') {
    return value
      ? <span className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700">Yes</span>
      : <span className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">No</span>;
  }
  if (Array.isArray(value) && value.length > 0 && value.every(v => typeof v === 'string')) {
    return (
      <div className="flex flex-wrap gap-1">
        {value.map((v, i) => (
          <span key={i} className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-indigo-100 text-indigo-700 dark:text-indigo-300">
            {isEnumValue(v) ? formatEnumValue(v) : v}
          </span>
        ))}
      </div>
    );
  }
  if (typeof value === 'string' && isEnumValue(value)) {
    return <span className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-indigo-100 text-indigo-700 dark:text-indigo-300">{formatEnumValue(value)}</span>;
  }
  if (typeof value === 'string') {
    return <span className="text-xs text-gray-800 dark:text-gray-100 break-words">{value}</span>;
  }
  return <span className="text-xs text-gray-800 dark:text-gray-100 break-words">{JSON.stringify(value)}</span>;
}

/** Detect if value is a structured object (not a plain string/number). */
function isStructuredObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) &&
    Object.keys(value).length > 2;
}

/** Detect if value looks like an array of structured records or a single structured object. */
function isRecordArray(value: unknown): value is Array<Record<string, unknown>> {
  if (isStructuredObject(value)) return false; // single object handled separately
  if (!Array.isArray(value) || value.length === 0) return false;
  const first = value[0];
  return typeof first === 'object' && first !== null &&
    ('firstName' in first || 'name' in first || 'isGroup' in first || 'isOrganization' in first || 'violationType' in first);
}

const violationTypeTitles: Record<string, string> = {
  KILLING: 'Killing', CRIMINALIZATION: 'Criminalization', OTHER: 'Other',
};

function getRecordTitle(obj: Record<string, unknown>): string {
  if (obj.violationType) return violationTypeTitles[String(obj.violationType)] || String(obj.violationType);
  if (obj.firstName || obj.lastName) {
    return [obj.firstName, obj.lastName].filter(Boolean).join(' ');
  }
  if (obj.name) return String(obj.name);
  if (obj.groupName && Array.isArray(obj.groupName) && obj.groupName.length > 0) {
    return (obj.groupName as string[]).join(', ');
  }
  return 'Record';
}

function RecordCard({ record, className }: { record: Record<string, unknown>; className?: string }) {
  const title = getRecordTitle(record);
  const titleKeys = new Set(['First Name', 'Last Name', 'Name', 'Violation Type']);
  const fields = summarizeRecord(record);
  return (
    <div className={`bg-white dark:bg-gray-800/60 rounded-md p-2 border border-gray-200 dark:border-gray-700/50 ${className || ''}`}>
      <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1">{title}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {fields.filter(f => !titleKeys.has(f.label)).map(f => (
          <React.Fragment key={f.label}>
            <dt className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{f.label}</dt>
            <dd><FieldValue value={f.raw} /></dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
}

function RichValue({ value, className }: { value: unknown; className?: string }) {
  if (isStructuredObject(value)) {
    return <RecordCard record={value as Record<string, unknown>} className={className} />;
  }

  if (isRecordArray(value)) {
    return (
      <div className={`space-y-2 ${className || ''}`}>
        {value.map((record, i) => (
          <RecordCard key={i} record={record} className={className} />
        ))}
      </div>
    );
  }

  // Simple string array → badges
  if (Array.isArray(value) && value.length > 0 && value.every(v => typeof v === 'string')) {
    return (
      <div className={`flex flex-wrap gap-1 ${className || ''}`}>
        {value.map((v, i) => (
          <span key={i} className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-indigo-100 text-indigo-700 dark:text-indigo-300">
            {isEnumValue(v) ? formatEnumValue(v) : v}
          </span>
        ))}
      </div>
    );
  }

  // Single enum string → badge
  if (typeof value === 'string' && isEnumValue(value)) {
    return <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-indigo-100 text-indigo-700 dark:text-indigo-300 ${className || ''}`}>{formatEnumValue(value)}</span>;
  }

  // Boolean
  if (typeof value === 'boolean') {
    return value
      ? <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700 ${className || ''}`}>Yes</span>
      : <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 ${className || ''}`}>No</span>;
  }

  const formatted = formatValue(value);
  if (formatted === 'none') {
    return <span className={`italic text-gray-400 dark:text-gray-500 text-xs ${className || ''}`}>none</span>;
  }
  return <span className={`text-xs text-gray-800 dark:text-gray-100 ${className || ''}`}>{formatted}</span>;
}

// Derive the suggestion type for a fix (mirrors the merge-suggestion taxonomy):
//   Add     — a new child record (victim/perpetrator/violation detail)
//   Fill    — the field is currently empty
//   Conflict — the field already has a different value
function deriveFixType(field: string, currentValue: unknown): { label: string; color: string } {
  if (['victims', 'perpetrators', 'violationDetails'].includes(field)) return { label: 'Add', color: 'bg-green-100 text-green-700' };
  const empty = currentValue === null || currentValue === undefined || currentValue === '' || (Array.isArray(currentValue) && currentValue.length === 0);
  return empty ? { label: 'Fill', color: 'bg-blue-100 text-blue-700' } : { label: 'Conflict', color: 'bg-red-100 text-red-700' };
}

function FindingCard({ finding, caseData, onApply, applyPending, defaultOpen = false }: {
  finding: Finding;
  caseData?: Record<string, any>;
  onApply?: () => void;
  applyPending: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const isBlocker = finding.severity === 'blocker';
  const fixes = finding.fixes ?? [];
  const hasFixes = fixes.length > 0;
  const allApplied = hasFixes && fixes.every(f => f.applied);
  const pendingFixes = fixes.filter(f => !f.applied);

  const cardBorder = allApplied
    ? 'border border-green-200'
    : isBlocker
    ? 'border border-red-200'
    : 'border border-yellow-200';

  const headerBg = allApplied
    ? 'bg-green-50'
    : isBlocker
    ? 'bg-red-50'
    : 'bg-yellow-50';

  const severityBadge = isBlocker
    ? <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700 shrink-0"><XCircleIcon className="h-3 w-3" />Blocker</span>
    : <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium rounded bg-yellow-100 text-yellow-700 shrink-0"><ExclamationTriangleIcon className="h-3 w-3" />Note</span>;

  return (
    <li className={`text-sm rounded-md overflow-hidden ${cardBorder}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-start gap-2 p-3 text-left ${headerBg}`}
      >
        <div className="mt-0.5">{severityBadge}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`${isBlocker ? 'text-red-800' : 'text-gray-800 dark:text-gray-100'}`}>{finding.message}</span>
            {hasFixes && allApplied && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700"><CheckCircleIcon className="h-3 w-3" />Applied</span>
            )}
            {hasFixes && !allApplied && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700" title="Expand to see suggestion"><QuestionMarkCircleIcon className="h-3 w-3" />{fixes.length === 1 ? 'Suggestion below' : `${fixes.length} suggestions below`}</span>
            )}
          </div>
        </div>
        {open ? <ChevronUpIcon className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 shrink-0 mt-0.5" /> : <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 shrink-0 mt-0.5" />}
      </button>
      {open && (
        <div className="p-4 pl-9">
          {hasFixes && (
            <div className="space-y-3">
              {fixes.map((fix, i) => {
                const currentValue = caseData ? getValueByPath(caseData, fix.field) : undefined;
                const fixType = deriveFixType(fix.field, currentValue);
                return (
                  <div key={i}>
                    <p className="text-xs font-medium text-blue-700 mb-1.5 flex items-center gap-1.5">
                      <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${fixType.color}`}>{fixType.label}</span>
                      {formatFieldPath(fix.field, caseData)}
                    </p>
                    {fix.applied ? (
                      <div>
                        <RichValue value={fix.suggestedValue} />
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircleIcon className="h-3 w-3 inline mr-0.5" />
                          Applied{fix.appliedAt ? ` ${formatDistanceToNow(new Date(fix.appliedAt), { addSuffix: true })}` : ''}
                        </p>
                      </div>
                    ) : isRecordArray(currentValue) || isRecordArray(fix.suggestedValue) ? (
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Current:</p>
                          <RichValue value={currentValue} />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-blue-600 mb-1">Suggested:</p>
                          <RichValue value={fix.suggestedValue} />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3 text-xs">
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-gray-500 dark:text-gray-400">Current:</span>
                          <span className={`ml-1 ${formatValue(currentValue) === 'none' ? 'italic text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
                            {formatValue(currentValue)}
                          </span>
                        </div>
                        <span className="text-gray-400 dark:text-gray-500 shrink-0">&rarr;</span>
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-blue-600">Suggested:</span>
                          <RichValue value={fix.suggestedValue} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {(finding.snippet || finding.source) && (
                <div className="pt-2 mt-1 border-t border-gray-100">
                  {finding.snippet && (
                    <p className="text-xs italic text-gray-500 dark:text-gray-400 mb-1">&ldquo;{finding.snippet}&rdquo;</p>
                  )}
                  {finding.source && (
                    <a href={finding.source} target="_blank" rel="noopener noreferrer" className="flex items-start gap-1.5 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 underline break-all">
                      <svg className="h-3.5 w-3.5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" /></svg>
                      {finding.source}
                    </a>
                  )}
                </div>
              )}
              <div>
                {allApplied ? (
                  <span className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-green-100 text-green-700">
                    <CheckCircleIcon className="h-3.5 w-3.5" />
                    All fixes applied
                  </span>
                ) : onApply && pendingFixes.length > 0 ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); onApply(); }}
                    disabled={applyPending}
                    className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {pendingFixes.length === 1 ? 'Apply Fix' : `Apply ${pendingFixes.length} Fixes`}
                  </button>
                ) : null}
              </div>
            </div>
          )}
          {!hasFixes && (finding.snippet || finding.source) && (
            <div>
              {finding.snippet && (
                <p className="text-xs italic text-gray-500 dark:text-gray-400 mb-1">&ldquo;{finding.snippet}&rdquo;</p>
              )}
              {finding.source && (
                <a href={finding.source} target="_blank" rel="noopener noreferrer" className="flex items-start gap-1.5 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 underline break-all">
                  <svg className="h-3.5 w-3.5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" /></svg>
                  {finding.source}
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

interface AiValidationPanelProps {
  caseId: string;
  caseData?: Record<string, any>;
  onChange?: () => void;
}

export default function AiValidationPanel({ caseId, caseData, onChange }: AiValidationPanelProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerShown, setDrawerShown] = useState(false); // drives the slide-in/out animation
  const [drawerTab, setDrawerTab] = useState<DrawerTab>('all'); // internal nav tab inside the drawer
  const openDrawer = () => { setDrawerTab('all'); setModalOpen(true); requestAnimationFrame(() => setDrawerShown(true)); };
  const closeDrawer = () => { setDrawerShown(false); setTimeout(() => setModalOpen(false), 300); };
  // Completeness details — its own slide-over (mirrors the AI Validation / AI Suggestions drawer).
  const [compShown, setCompShown] = useState(false);
  const openCompleteness = () => { setShowCompleteness(true); requestAnimationFrame(() => setCompShown(true)); };
  const closeCompleteness = () => { setCompShown(false); setTimeout(() => setShowCompleteness(false), 300); };
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [undoingField, setUndoingField] = useState<string | null>(null);
  // Inline override editor (Auto-Filled list): which field is being edited, its draft value, saving.
  const [editField, setEditField] = useState<string | null>(null);
  const [editVal, setEditVal] = useState<string>('');
  const [savingOverride, setSavingOverride] = useState(false);
  // A fill into an empty field has no meaningful "former value" — only enrichments/overwrites do.
  const hadPriorValue = (v: unknown): boolean => {
    if (v === null || v === undefined) return false;
    if (typeof v === 'string') return !['', 'not specified', 'unknown', 'n/a'].includes(v.trim().toLowerCase());
    if (Array.isArray(v)) return v.length > 0;
    return true;
  };
  // Auto-fill detail popup (snippet / source / former value), one entry at a time — mirrors the drawer.
  const [detailEntry, setDetailEntry] = useState<any | null>(null);
  const [detailShown, setDetailShown] = useState(false);
  const openDetail = (a: any) => { setDetailEntry(a); requestAnimationFrame(() => setDetailShown(true)); };
  const closeDetail = () => { setDetailShown(false); setTimeout(() => setDetailEntry(null), 300); };
  const [showCompleteness, setShowCompleteness] = useState(false);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const { user } = useAuth();

  // Validation runs async: the trigger returns 202 immediately while the worker processes in the
  // background. Track when we triggered so the button stays disabled (and we poll) until a fresh
  // result comes back — otherwise the button re-enables instantly and the user can double-fire it.
  const [triggeredAt, setTriggeredAt] = useState<number | null>(null);

  const { data: validationDetail, isLoading } = useQuery<ValidationStatusResponse>({
    queryKey: ['validation-status', caseId],
    queryFn: () => validationApi.getStatus(caseId),
    refetchInterval: triggeredAt ? 5000 : false,
  });

  // Clear the in-flight flag once a newer result has landed, or after a safety cap (so a failed
  // run can't leave the button stuck).
  useEffect(() => {
    if (!triggeredAt) return;
    const latestTs = validationDetail?.latest?.createdAt ? new Date(validationDetail.latest.createdAt).getTime() : 0;
    if (latestTs > triggeredAt) { setTriggeredAt(null); return; }
    const cap = setTimeout(() => setTriggeredAt(null), 120000);
    return () => clearTimeout(cap);
  }, [triggeredAt, validationDetail]);

  const validateMutation = useMutation({
    mutationFn: () => validationApi.validateCase(caseId, user?.id),
    onSuccess: (data) => {
      setTriggeredAt(Date.now());
      if ('status' in data && data.status === 'processing') {
        showToast('success', 'Validation started — results will appear shortly');
      } else {
        showToast('success', 'Validation completed');
      }
      queryClient.invalidateQueries({ queryKey: ['validation-status', caseId] });
    },
    onError: () => showToast('error', 'Validation failed'),
  });

  const applySuggestionsMutation = useMutation({
    mutationFn: (fields?: string[]) =>
      validationApi.applySuggestions(validationDetail!.latest!.id, fields),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['validation-status', caseId] });
      const count = data.applied.length;
      showToast('success', `Applied ${count} suggestion${count !== 1 ? 's' : ''}`);
    },
    onError: () => showToast('error', 'Failed to apply suggestions'),
  });

  const isValidating = caseData?.validationStatus === 'VALIDATING' || validateMutation.isPending || triggeredAt != null;

  // Confirm before spending tokens; guard against double-fire while a run is in flight.
  const handleValidate = async () => {
    if (isValidating) return;
    const ok = await confirm({
      title: 'Re-run AI validation?',
      message: 'This runs an AI validation that uses tokens (billable) and can take up to a minute. Only run it when needed. Proceed?',
      confirmLabel: 'Proceed',
      cancelLabel: 'Cancel',
    });
    if (!ok) return;
    validateMutation.mutate();
  };

  const latest = validationDetail?.latest;
  const historyCount = (validationDetail?.history?.length ?? 0) - 1;

  const findings = [...(latest?.aiResponse?.findings ?? [])].sort((a, b) => {
    const sevOrder = { blocker: 0, note: 1 };
    const sevA = sevOrder[a.severity] ?? 2;
    const sevB = sevOrder[b.severity] ?? 2;
    return sevA - sevB;
  });
  const fixable = findings.filter(f => f.fixes && f.fixes.length > 0);
  const pendingFixCount = findings.reduce((sum, f) => sum + (f.fixes ?? []).filter(fix => !fix.applied).length, 0);

  // Auto-applied fields (auto-apply feature): AI filled these into the record; shown here as the
  // run's ledger (also marked inline on the fields). Undo reverts to the prior value.
  const applied: any[] = ((caseData?.metadata?.ai?.autoApplied as any[]) ?? []).filter((a) => !a.undone && !a.superseded);
  const undoneEntries: any[] = ((caseData?.metadata?.ai?.autoApplied as any[]) ?? []).filter((a) => a.undone);
  // Map each field -> the finding's message + severity/category, from the stored result. This lets
  // the detail popup show severity/category for auto-fills and conflicts (whose stored metadata entry
  // may predate the backend that records them) by deriving from the finding that produced the fix.
  const messageByField: Record<string, string> = {};
  const metaByField: Record<string, { severity?: string; category?: string }> = {};
  for (const f of (latest?.aiResponse?.findings ?? [])) {
    const msg = (f as any).message;
    const meta = { severity: (f as any).severity, category: (f as any).category };
    const put = (fld?: string) => { if (fld) { if (msg) messageByField[fld] = msg; metaByField[fld] = meta; } };
    put((f as any).field);
    for (const fix of (f.fixes ?? [])) put(fix.field);
  }
  const undoApplied = async (field: string) => {
    setUndoingField(field);
    try {
      await validationApi.undoAutoApplied(caseId, field);
      showToast('success', 'Change undone');
      onChange?.();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Undo failed');
    } finally {
      setUndoingField(null);
    }
  };
  const redoApplied = async (field: string) => {
    setUndoingField(field);
    try {
      await validationApi.redoAutoApplied(caseId, field);
      showToast('success', 'Change re-applied');
      onChange?.();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Redo failed');
    } finally {
      setUndoingField(null);
    }
  };

  // Inline override of an auto-filled value. Scalar/boolean/array only — entity adds (new victim,
  // etc.) and nested objects are edited on the case form instead.
  const OVERRIDE_ADD_FIELDS = new Set(['victims', 'violationDetails', 'perpetrators', 'additionalSources']);
  // "Type" fields (violationTypes, otherViolationTypes, perpetratorTypes, …) are controlled enums —
  // they must be edited via the case form's dropdowns, not free-typed here.
  const isTypeField = (field: string) => /type/i.test((field.split('.').pop() || field).replace(/\[.*?\]/g, ''));
  const isOverridable = (a: any) => !OVERRIDE_ADD_FIELDS.has(a.field) && !isTypeField(a.field) && !(a.value && typeof a.value === 'object' && !Array.isArray(a.value));
  const startEdit = (a: any) => {
    setEditField(a.field);
    const v = a.value;
    setEditVal(Array.isArray(v) ? v.join(', ') : (v == null ? '' : String(v)));
  };
  const coerceOverride = (orig: any, s: string): any => {
    if (typeof orig === 'number') { const n = Number(s); return Number.isNaN(n) ? s : n; }
    if (typeof orig === 'boolean') return s === 'true';
    if (Array.isArray(orig)) return s.split(',').map((x) => x.trim()).filter(Boolean);
    return s;
  };
  const saveOverride = async (a: any) => {
    setSavingOverride(true);
    try {
      await validationApi.overrideAutoApplied(caseId, a.field, coerceOverride(a.value, editVal));
      showToast('success', 'Value updated');
      setEditField(null);
      onChange?.();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Update failed');
    } finally {
      setSavingOverride(false);
    }
  };

  // Source conflicts (auto-apply feature): unresolved disagreements that block approval.
  const conflicts: any[] = ((caseData?.metadata?.ai?.conflicts as any[]) ?? []).filter((c) => !c.resolved);
  const canResolve = user?.role === 'ADMIN' || user?.role === 'APPROVER';
  const resolveConflict = async (id: string, choice: 'current' | 'proposed') => {
    setResolvingId(`${id}:${choice}`);
    try {
      await validationApi.resolveConflict(caseId, id, choice);
      showToast('success', 'Conflict resolved');
      onChange?.();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Failed to resolve conflict');
    } finally {
      setResolvingId(null);
    }
  };
  const fmtVal = (v: unknown): React.ReactNode => {
    if (v === null || v === undefined || v === '') return <span className="italic text-gray-400 dark:text-gray-500">empty</span>;
    const asDate = formatIsoDate(v);
    if (asDate) return asDate;
    if (Array.isArray(v)) return v.map((x) => (x && typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };
  // Source URL as link text, protocol stripped and truncated to 50 chars.
  const shortSource = (u: string) => {
    const s = u.replace(/^https?:\/\/(www\.)?/, '');
    return s.length > 75 ? s.slice(0, 75) + '…' : s;
  };

  // A new victim/perpetrator/violation-detail record is added as an object — summarize it in
  // plain language instead of dumping JSON.
  const ADD_LABELS: Record<string, string> = { victims: 'victim', perpetrators: 'perpetrator', violationDetails: 'violation detail' };
  const entityName = (o: any): string => {
    if (!o || typeof o !== 'object') return String(o);
    const nm = [o.firstName, o.lastName].filter(Boolean).join(' ');
    if (nm) return nm;
    if (o.name) return String(o.name);
    if (o.groupName) return Array.isArray(o.groupName) ? o.groupName.join(', ') : String(o.groupName);
    if (o.violationType) return String(o.violationType);
    return 'record';
  };
  // Render an auto-applied value as the visual focal point of its row:
  //  - entity add  -> "New victim: <name>"
  //  - array/enum  -> chips, with elements NOT in the prior value highlighted (what changed)
  //  - scalar/text -> the value, prominent
  const renderAppliedValue = (a: any): React.ReactNode => {
    const { field, value, priorValue } = a;
    const addLabel = ADD_LABELS[field];
    if (addLabel) {
      const arr = Array.isArray(value) ? value : [value];
      return (
        <span className="inline-flex items-center gap-1 rounded bg-violet-50 dark:bg-violet-900/30 px-1.5 py-0.5 font-medium text-violet-800 dark:text-violet-300">
          + New {addLabel}: {arr.map(entityName).join('; ')}
        </span>
      );
    }
    if (Array.isArray(value)) {
      const prior = new Set((Array.isArray(priorValue) ? priorValue : []).map((x) => String(x)));
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((v, i) => {
            const isNew = !prior.has(String(v));
            const text = isEnumValue(String(v)) ? formatEnumValue(String(v)) : String(v);
            return (
              <span key={i} className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${isNew ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-300 ring-1 ring-violet-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                {isNew ? `+ ${text}` : text}
              </span>
            );
          })}
        </div>
      );
    }
    return <span className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">{fmtVal(value)}</span>;
  };

  // When auto-apply is active, applyable findings are already shown inline (auto-applied) or as
  // conflicts above — so the findings list collapses to just the informational items the AI could
  // not act on (no corroboration, "consider adding a victim", etc.).
  const allConflictFields = new Set(((caseData?.metadata?.ai?.conflicts as any[]) ?? []).map((c) => c.field).filter(Boolean));
  const autoApplyActive = ((caseData?.metadata?.ai?.autoApplied as any[])?.length ?? 0) > 0 || allConflictFields.size > 0;
  const visibleFindings = autoApplyActive
    ? findings.filter((f) => {
        const fixes = f.fixes ?? [];
        const ff = (f as any).field;
        const allApplied = fixes.length > 0 && fixes.every((x: any) => x.applied);
        const isConflict = (ff && allConflictFields.has(ff)) || fixes.some((x: any) => allConflictFields.has(x.field));
        return !allApplied && !isConflict;
      })
    : findings;

  // Blocker-severity findings without an auto-fix (e.g. a data conflict the AI couldn't
  // resolve) must stand out — surface them in their own section, not buried in "Other comments".
  const visibleBlockers = visibleFindings.filter((f) => f.severity === 'blocker');
  const visibleNotes = visibleFindings.filter((f) => f.severity !== 'blocker');
  // In auto-apply mode blockers get their own section, so "Other comments" holds only notes.
  const otherCommentsList = autoApplyActive ? visibleNotes : visibleFindings;

  // Summary badge counts: when auto-apply is active, count only what still needs a human
  // (applied + conflicts are shown separately) — the remaining informational notes/blockers.
  const badgeSource = autoApplyActive ? visibleFindings : findings;
  const blockerCount = badgeSource.filter((f) => f.severity === 'blocker').length;
  const noteCount = badgeSource.filter((f) => f.severity === 'note').length;
  const fixCount = autoApplyActive ? 0 : fixable.length; // fixes are auto-applied when active

  // Internal drawer tabs — turn the long scroll into a few focused views. Only tabs with content
  // appear (besides "All"); the active tab falls back to "All" if its content drops to zero.
  const drawerTabDefs: { key: DrawerTab; label: string; count: number; pill: string }[] = [];
  if (latest) {
    const reviewCount = (autoApplyActive ? visibleBlockers.length : 0) + conflicts.length + applied.length + otherCommentsList.length;
    drawerTabDefs.push({ key: 'all', label: 'All', count: reviewCount, pill: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' });
    if (autoApplyActive && visibleBlockers.length > 0) drawerTabDefs.push({ key: 'blockers', label: 'Blockers', count: visibleBlockers.length, pill: 'bg-red-100 text-red-700' });
    if (conflicts.length > 0) drawerTabDefs.push({ key: 'resolution', label: 'For Resolution', count: conflicts.length, pill: 'bg-amber-100 text-amber-800' });
    if (applied.length > 0) drawerTabDefs.push({ key: 'autofilled', label: 'Auto-Filled', count: applied.length, pill: 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300' });
    if (otherCommentsList.length > 0) drawerTabDefs.push({ key: 'notes', label: autoApplyActive ? 'Notes' : 'Findings', count: otherCommentsList.length, pill: 'bg-yellow-100 text-yellow-700' });
    if ((latest.aiResponse?.sources?.length ?? 0) > 0) drawerTabDefs.push({ key: 'sources', label: 'Sources', count: latest.aiResponse!.sources!.length, pill: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' });
    if (historyCount > 0) drawerTabDefs.push({ key: 'history', label: 'History', count: historyCount, pill: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' });
  }
  const activeDrawerTab: DrawerTab = drawerTabDefs.some((t) => t.key === drawerTab) ? drawerTab : 'all';
  const showTab = (t: DrawerTab) => activeDrawerTab === 'all' || activeDrawerTab === t;

  return (
    <>
      {/* Sidebar Summary Card */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">AI Assisted Verification</h3>
          {isValidating ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
              <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
              Validating
            </span>
          ) : latest ? (
            <div className="flex items-center gap-1.5">
              {decisionIcons[latest.decision]}
              <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${decisionColors[latest.decision]}`}>
                {decisionLabel(latest.decision)}
              </span>
            </div>
          ) : null}
        </div>

        {isValidating && !latest ? (
          <div className="text-center py-4">
            <ArrowPathIcon className="h-8 w-8 mx-auto text-blue-400 animate-spin mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Validation in progress...</p>
          </div>
        ) : isLoading ? (
          <div className="animate-pulse space-y-2">
            <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded w-1/2" />
            <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded" />
          </div>
        ) : latest ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              {labeledBar('Confidence', confidenceBar(latest.confidence))}
              {labeledBar('Documentation', completenessBar(latest.aiResponse?.completeness))}
            </div>
            {(findings.length > 0 || conflicts.length > 0 || applied.length > 0) && (
              <div className="flex flex-wrap gap-1.5">
                {applied.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300">
                    {applied.length} Auto-fill from relevant sources
                  </span>
                )}
                {conflicts.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                    {conflicts.length} conflict{conflicts.length !== 1 ? 's' : ''} to resolve
                  </span>
                )}
                {blockerCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                    {blockerCount} blocker{blockerCount !== 1 ? 's' : ''}
                  </span>
                )}
                {noteCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                    {noteCount} note{noteCount !== 1 ? 's' : ''}
                  </span>
                )}
                {fixCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                    {fixCount} fix{fixCount !== 1 ? 'es' : ''}
                  </span>
                )}
              </div>
            )}
            {latest.aiResponse?.reasoning && (
              <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-4">{latest.aiResponse.reasoning}</p>
            )}
            <button
              onClick={openDrawer}
              className="w-full px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 rounded-md hover:bg-indigo-100"
            >
              View Details
            </button>
          </div>
        ) : (
          <div className="text-center py-2">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">No validation results</p>
            <button
              onClick={handleValidate}
              disabled={isValidating}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowPathIcon className={`h-4 w-4 ${isValidating ? 'animate-spin' : ''}`} />
              {isValidating ? 'Validating...' : 'Run Validation'}
            </button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-gray-500/60 transition-opacity duration-300 ${drawerShown ? 'opacity-100' : 'opacity-0'}`}
            onClick={closeDrawer}
          />
          {/* Right slide-over drawer */}
          <div
            className={`fixed inset-y-0 right-0 w-full max-w-4xl bg-white dark:bg-gray-800 shadow-2xl flex flex-col transition-transform duration-300 ease-out ${drawerShown ? 'translate-x-0' : 'translate-x-full'}`}
          >
            {/* Drawer Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">AI Assisted Verification Details</h3>
              <button
                onClick={closeDrawer}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Drawer Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                {latest ? (
                  <>
                    {/* Decision, Confidence & Completeness */}
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {decisionIcons[latest.decision]}
                          <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${decisionColors[latest.decision]}`}>
                            {decisionLabel(latest.decision)}
                          </span>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">Confidence</div>
                            {confidenceBar(latest.confidence, 'lg')}
                          </div>
                          {latest.aiResponse?.completeness && (
                            <div className="text-right">
                              <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">Documentation</div>
                              {completenessBar(latest.aiResponse.completeness, 'lg')}
                            </div>
                          )}
                        </div>
                      </div>
                      <ScoreBreakdown breakdown={latest.aiResponse?.confidenceBreakdown} />
                      {/* Completeness details — opens a popup */}
                      {latest.aiResponse?.completeness && (
                        <button
                          onClick={openCompleteness}
                          className="flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800"
                        >
                          Show completeness details
                        </button>
                      )}
                    </div>

                    {/* Internal navigation tabs — focus the view instead of one long scroll */}
                    {drawerTabDefs.length > 1 && (
                      <div className="border-b border-gray-200 dark:border-gray-700">
                        <nav className="-mb-px flex gap-4 overflow-x-auto">
                          {drawerTabDefs.map((t) => (
                            <button
                              key={t.key}
                              onClick={() => setDrawerTab(t.key)}
                              className={`whitespace-nowrap pb-2.5 pt-1 text-sm font-medium border-b-2 flex items-center gap-1.5 transition-colors ${
                                activeDrawerTab === t.key
                                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300'
                              }`}
                            >
                              {t.label}
                              <span className={`inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-xs font-semibold ${t.pill}`}>{t.count}</span>
                            </button>
                          ))}
                        </nav>
                      </div>
                    )}

                    {/* Reasoning */}
                    {activeDrawerTab === 'all' && latest.aiResponse?.reasoning && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">AI Reasoning</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{latest.aiResponse.reasoning}</p>
                      </div>
                    )}

                    {/* Blockers without an auto-fix — surfaced prominently at the TOP, always expanded */}
                    {showTab('blockers') && autoApplyActive && visibleBlockers.length > 0 && (
                      <div className="rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                        <div className="flex items-center gap-1.5 mb-2">
                          <ExclamationTriangleIcon className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0" />
                          <span className="text-sm font-semibold text-red-800 dark:text-red-300">
                            Blocker{visibleBlockers.length !== 1 ? 's' : ''} ({visibleBlockers.length}) — resolve before approving
                          </span>
                        </div>
                        <ul className="space-y-1.5">
                          {visibleBlockers.map((finding, idx) => {
                            const src = (finding as any).source as string | undefined;
                            return (
                              <li key={idx} className="rounded-md border border-red-200 dark:border-red-800 bg-white dark:bg-gray-800 p-3 text-xs">
                                <div className="text-gray-800 dark:text-gray-200"><span className="text-gray-400 dark:text-gray-500 font-medium">{idx + 1}.</span> {finding.message}</div>
                                {(src || (finding as any).snippet) && (
                                  <div className="mt-1.5 flex justify-start">
                                    <button onClick={() => openDetail(finding)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                                      <InformationCircleIcon className="h-3.5 w-3.5" /> Show details
                                    </button>
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* Source conflicts — sources disagree on an existing value; blocks approval */}
                    {showTab('resolution') && conflicts.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-2 flex items-center gap-1">
                          <ExclamationTriangleIcon className="h-4 w-4" /> {conflicts.length} source conflict{conflicts.length !== 1 ? 's' : ''} to resolve — blocks approval
                        </p>
                        <div className="space-y-2">
                          {conflicts.map((c, i) => (
                            <div key={c.id || i} className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs">
                              {/* description */}
                              {(c.message || messageByField[c.field]) && <div className="text-gray-700 dark:text-gray-300 mb-1.5">{c.message || messageByField[c.field]}</div>}
                              {/* field */}
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-1"><span className="text-amber-500 dark:text-amber-400">{i + 1}.</span> {formatFieldPath(c.field || 'field', caseData)}</div>
                              {/* value — Current → Proposed comparison, weighted toward the proposal */}
                              <div className="grid grid-cols-[1fr_auto_1.15fr] items-stretch gap-2 mb-2">
                                <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/60 p-2">
                                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">Current</div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400 line-through break-words">{fmtVal(c.currentValue)}</div>
                                </div>
                                <div className="flex items-center text-gray-400 dark:text-gray-500"><ArrowRightIcon className="h-4 w-4" /></div>
                                <div className="rounded-md border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/25 p-2">
                                  <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-0.5">Proposed</div>
                                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-words">{fmtVal(c.proposedValue)}</div>
                                </div>
                              </div>
                              {/* actions — details for everyone; resolve only for approvers */}
                              {(c.snippet || c.source || (canResolve && c.id)) && (
                                <div className="flex items-center justify-between gap-2">
                                  <div>
                                    {(c.snippet || c.source) && (
                                      <button onClick={() => openDetail(c)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100">
                                        <InformationCircleIcon className="h-3.5 w-3.5" /> Show details
                                      </button>
                                    )}
                                  </div>
                                  {canResolve && c.id && (
                                    <div className="flex items-center gap-2">
                                      <button disabled={!!resolvingId} onClick={() => resolveConflict(c.id, 'current')} className="px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 disabled:opacity-50">Keep current</button>
                                      <button disabled={!!resolvingId} onClick={() => resolveConflict(c.id, 'proposed')} className="px-2.5 py-1 rounded-md bg-indigo-600 font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">Use proposed</button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Auto-applied fields — AI filled these; also marked inline on the fields */}
                    {showTab('autofilled') && applied.length > 0 && (
                      <div>
                        <div className="mb-2">
                          <p className="text-sm font-medium text-violet-700 dark:text-violet-300 flex items-center gap-1">
                            <SparklesIcon className="h-4 w-4" /> {applied.length} field{applied.length !== 1 ? 's' : ''} from related sources auto-filled by AI
                          </p>
                        </div>
                        <div className="space-y-1.5">
                          {applied.map((a, i) => (
                            <div key={i} className="rounded-md border border-gray-200 dark:border-gray-700 p-3 text-xs">
                              {/* description */}
                              {(a.message || messageByField[a.field]) && (
                                <div className="text-gray-600 dark:text-gray-400 mb-1.5">{a.message || messageByField[a.field]}</div>
                              )}
                              {/* field */}
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1"><span className="text-gray-400 dark:text-gray-500">{i + 1}.</span> {formatFieldPath(a.field, caseData)}</div>
                              {editField === a.field ? (
                                /* Inline override editor */
                                <div className="mb-1">
                                  {typeof a.value === 'boolean' ? (
                                    <select value={editVal} onChange={(e) => setEditVal(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100">
                                      <option value="true">Yes</option>
                                      <option value="false">No</option>
                                    </select>
                                  ) : (
                                    <textarea
                                      value={editVal}
                                      onChange={(e) => setEditVal(e.target.value)}
                                      rows={Array.isArray(a.value) || String(a.value ?? '').length > 40 ? 3 : 1}
                                      autoFocus
                                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                                    />
                                  )}
                                  {Array.isArray(a.value) && <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Separate multiple values with commas.</p>}
                                  <div className="flex items-center justify-end gap-2 mt-1.5">
                                    <button onClick={() => setEditField(null)} disabled={savingOverride} className="px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 disabled:opacity-50">Cancel</button>
                                    <button onClick={() => saveOverride(a)} disabled={savingOverride} className="px-2.5 py-1 rounded-md bg-indigo-600 font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">{savingOverride ? 'Saving…' : 'Save'}</button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  {/* value — the AI-applied (proposed) value */}
                                  <div className="text-sm mb-1.5">{renderAppliedValue(a)}</div>
                                  {/* actions — Show details on the left; Edit (primary) + Undo (secondary) on the right */}
                                  <div className="flex items-center justify-between gap-2">
                                    <button onClick={() => openDetail(a)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                                      <InformationCircleIcon className="h-3.5 w-3.5" /> Show details
                                    </button>
                                    <div className="flex items-center gap-2">
                                    {canResolve && (
                                      <button disabled={undoingField === a.field} onClick={() => undoApplied(a.field)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
                                        <ArrowPathIcon className="h-4 w-4" /> Undo
                                      </button>
                                    )}
                                    {canResolve && isOverridable(a) && (
                                      <button onClick={() => startEdit(a)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-indigo-600 font-semibold text-white hover:bg-indigo-700">
                                        <PencilSquareIcon className="h-3.5 w-3.5" /> Edit
                                      </button>
                                    )}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Undone auto-applies — re-appliable */}
                    {showTab('autofilled') && undoneEntries.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-400 dark:text-gray-500 mb-1">{undoneEntries.length} undone</p>
                        <div className="space-y-1">
                          {undoneEntries.map((a, i) => (
                            <div key={i} className="rounded-md border border-dashed border-gray-200 dark:border-gray-700 p-2 text-xs flex items-center justify-between gap-2">
                              <span className="text-gray-400 dark:text-gray-500 line-through truncate">{formatFieldPath(a.field, caseData)}</span>
                              {canResolve && (
                                <button disabled={undoingField === a.field} onClick={() => redoApplied(a.field)} className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/30 font-semibold text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 disabled:opacity-50">
                                  <ArrowPathIcon className="h-4 w-4" /> Redo
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Findings (auto-apply active → informational notes only) */}
                    {showTab('notes') && otherCommentsList.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {autoApplyActive ? 'Other comments' : 'Findings'} ({otherCommentsList.length})
                          </span>
                          {!autoApplyActive && pendingFixCount > 0 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">{pendingFixCount} pending</span>
                          )}
                        </div>
                        <ul className="mt-2 space-y-1.5">
                          {otherCommentsList.map((finding, idx) => {
                            // Auto-apply mode: notes are informational (no action) — render in the same
                            // description → source card format as the applied/conflict entries.
                            if (autoApplyActive) {
                              const isBlocker = finding.severity === 'blocker';
                              const src = (finding as any).source as string | undefined;
                              return (
                                <li key={idx} className="rounded-md border border-gray-200 dark:border-gray-700 p-3 text-xs">
                                  {isBlocker && (
                                    <div className="mb-1">
                                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-100 text-red-700">Blocker</span>
                                    </div>
                                  )}
                                  <div className="text-gray-700 dark:text-gray-300">
                                    <span className="text-gray-400 dark:text-gray-500 font-medium">{idx + 1}.</span>{' '}
                                    {/* If the note cites a source, the note text IS the hyperlink (no bare URL). */}
                                    {src ? (
                                      <a href={src} target="_blank" rel="noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 underline">{finding.message}</a>
                                    ) : finding.message}
                                  </div>
                                </li>
                              );
                            }
                            const fields = (finding.fixes ?? []).filter(f => !f.applied).map(f => f.field);
                            return (
                            <FindingCard
                              key={idx}
                              finding={finding}
                              caseData={caseData}
                              onApply={fields.length > 0 ? () => applySuggestionsMutation.mutate(fields) : undefined}
                              applyPending={applySuggestionsMutation.isPending}
                            />
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* Sources */}
                    {showTab('sources') && latest.aiResponse?.sources && latest.aiResponse.sources.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Sources ({latest.aiResponse.sources.length})
                        </span>
                        <ul className="mt-2 space-y-2">
                          {latest.aiResponse.sources.map((source, idx) => (
                            <li key={idx} className="bg-gray-50 dark:bg-gray-900 rounded-md p-3 flex items-start gap-2">
                              <span className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 shrink-0">
                                {source.type}
                              </span>
                              {/* The headline/title is the hyperlink — no bare URL shown. */}
                              <a
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 underline break-words"
                                title={source.url}
                              >
                                {source.title || shortSource(source.url)}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Timestamp */}
                    {(activeDrawerTab === 'all' || activeDrawerTab === 'sources') && (
                      <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400">
                        <span>
                          Validated {formatDistanceToNow(new Date(latest.createdAt), { addSuffix: true })}
                        </span>
                        {latest.processingTimeMs > 0 && (
                          <span>Processing: {latest.processingTimeMs}ms</span>
                        )}
                      </div>
                    )}

                    {/* History */}
                    {showTab('history') && historyCount > 0 && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Previous Results ({historyCount})
                        </span>
                        <div className="mt-3 space-y-2">
                            {validationDetail!.history.slice(1).map((result) => (
                              <div
                                key={result.id}
                                className="flex items-center justify-between bg-gray-50 dark:bg-gray-900 rounded-lg px-4 py-3"
                              >
                                <div className="flex items-center gap-2">
                                  {decisionIcons[result.decision]}
                                  <span className="text-sm text-gray-700 dark:text-gray-300">{decisionLabel(result.decision)}</span>
                                </div>
                                <div className="flex items-center gap-4">
                                  {confidenceBar(result.confidence)}
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatDistanceToNow(new Date(result.createdAt), { addSuffix: true })}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                      </div>
                    )}

                    {/* Re-validate */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <button
                        onClick={handleValidate}
                        disabled={isValidating}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ArrowPathIcon className={`h-4 w-4 ${isValidating ? 'animate-spin' : ''}`} />
                        {isValidating ? 'Validating...' : 'Re-validate'}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    {isValidating ? (
                      <>
                        <ArrowPathIcon className="h-12 w-12 mx-auto text-blue-400 animate-spin mb-3" />
                        <p className="text-gray-500 dark:text-gray-400">Validation in progress...</p>
                      </>
                    ) : (
                      <>
                        <ShieldCheckIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                        <p className="text-gray-500 dark:text-gray-400">No validation results for this case</p>
                        <button
                          onClick={handleValidate}
                          disabled={isValidating}
                          className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ArrowPathIcon className="h-4 w-4" />
                          Run Validation
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
      )}

      {/* Completeness details — right slide-over (mirrors the AI Validation Details drawer) */}
      {showCompleteness && latest?.aiResponse?.completeness && (
        <div className="fixed inset-0 z-[60]">
          <div
            className={`fixed inset-0 bg-gray-500/60 transition-opacity duration-300 ${compShown ? 'opacity-100' : 'opacity-0'}`}
            onClick={closeCompleteness}
          />
          <div
            className={`fixed inset-y-0 right-0 w-full max-w-md bg-white dark:bg-gray-800 shadow-2xl flex flex-col transition-transform duration-300 ease-out ${compShown ? 'translate-x-0' : 'translate-x-full'}`}
          >
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between shrink-0">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Documentation coverage</h3>
              <button onClick={closeCompleteness} className="text-gray-400 dark:text-gray-500 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">How fully the case is documented against IPRI's Case Baseline / Data Dictionary. <span className="font-medium text-gray-600 dark:text-gray-300">For reference only</span> — a case can be approved without every field; the fields below simply weren't in the sources.</p>
              <div>
                <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">{latest.aiResponse.completeness.filled} of {latest.aiResponse.completeness.total} expected fields present</div>
                {completenessBar(latest.aiResponse.completeness, 'lg')}
              </div>
              {latest.aiResponse.completeness.anchorsPass !== undefined && (
                <div>
                  <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Required anchors</div>
                  <AnchorsBadge comp={latest.aiResponse.completeness} />
                </div>
              )}
              {latest.aiResponse.completeness.missing.length > 0 && (
                <div>
                  <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">Not documented ({latest.aiResponse.completeness.missing.length}) — optional</div>
                  <div className="space-y-2">
                    {groupMissing(latest.aiResponse.completeness.missing).map((g) => (
                      <div key={g.section}>
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">{g.section}</div>
                        <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-0.5 pl-1">
                          {g.items.map((it) => (
                            <li key={it.label} className="flex items-center gap-1.5">
                              <span className="text-gray-300 dark:text-gray-600">•</span>
                              {it.label}
                              {it.count > 1 && <span className="text-gray-400 dark:text-gray-500">×{it.count}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Auto-fill detail — right slide-over: snippet, source, and former value */}
      {detailEntry && (
        <div className="fixed inset-0 z-[60]">
          <div
            className={`fixed inset-0 bg-gray-500/60 transition-opacity duration-300 ${detailShown ? 'opacity-100' : 'opacity-0'}`}
            onClick={closeDetail}
          />
          <div
            className={`fixed inset-y-0 right-0 w-full max-w-md bg-white dark:bg-gray-800 shadow-2xl flex flex-col transition-transform duration-300 ease-out ${detailShown ? 'translate-x-0' : 'translate-x-full'}`}
          >
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between shrink-0">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Details</h3>
              <button onClick={closeDetail} className="text-gray-400 dark:text-gray-500 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 text-sm">
              {(() => {
                const sev = detailEntry.severity ?? metaByField[detailEntry.field]?.severity;
                const cat = detailEntry.category ?? metaByField[detailEntry.field]?.category;
                if (!sev && !cat) return null;
                return (
                  <div className="flex items-center gap-1.5">
                    {sev && (
                      <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded ${sev === 'blocker' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {sev === 'blocker' ? 'Blocker' : 'Note'}
                      </span>
                    )}
                    {cat && cat !== 'OTHER' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                        {String(cat).replace(/_/g, ' ').toLowerCase().replace(/^./, (c: string) => c.toUpperCase())}
                      </span>
                    )}
                  </div>
                );
              })()}
              {(detailEntry.message || messageByField[detailEntry.field]) && (
                <div>
                  <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Why</div>
                  <div className="text-gray-700 dark:text-gray-300">{detailEntry.message || messageByField[detailEntry.field]}</div>
                </div>
              )}
              {detailEntry.field && (
                <div>
                  <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Field</div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{formatFieldPath(detailEntry.field, caseData)}</div>
                </div>
              )}
              {detailEntry.proposedValue !== undefined ? (
                <div className="grid grid-cols-[1fr_auto_1.15fr] items-stretch gap-2">
                  <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/60 p-2.5">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">Current</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 line-through break-words">{fmtVal(detailEntry.currentValue)}</div>
                  </div>
                  <div className="flex items-center text-gray-400 dark:text-gray-500"><ArrowRightIcon className="h-4 w-4" /></div>
                  <div className="rounded-md border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/25 p-2.5">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-0.5">Proposed</div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-words">{fmtVal(detailEntry.proposedValue)}</div>
                  </div>
                </div>
              ) : detailEntry.value !== undefined ? (
                hadPriorValue(detailEntry.priorValue) ? (
                  <div className="grid grid-cols-[1fr_auto_1.15fr] items-stretch gap-2">
                    <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/60 p-2.5">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">Former value</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 line-through break-words">{fmtVal(detailEntry.priorValue)}</div>
                    </div>
                    <div className="flex items-center text-gray-400 dark:text-gray-500"><ArrowRightIcon className="h-4 w-4" /></div>
                    <div className="rounded-md border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/25 p-2.5">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-0.5">Applied value</div>
                      <div className="text-sm break-words">{renderAppliedValue(detailEntry)}</div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-md border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/25 p-2.5">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-0.5">Applied value</div>
                    <div className="text-sm break-words">{renderAppliedValue(detailEntry)}</div>
                  </div>
                )
              ) : null}
              {detailEntry.snippet && (
                <div>
                  <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Source snippet</div>
                  <div className="text-gray-600 dark:text-gray-300 italic">“{detailEntry.snippet}”</div>
                </div>
              )}
              {detailEntry.source && (
                <div>
                  <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Source</div>
                  <a href={detailEntry.source} target="_blank" rel="noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 underline break-all">{detailEntry.source}</a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
